"use client";

import type { GPOnboardingStepProps } from "@/components/NewGamePage/GPOnboarding/utils";
import { ActionItem } from "@/components/NewGamePage/GPOnboardingLinkTree/ActionItem";
import { ASSETS } from "@/components/NewGamePage/GPOnboardingLinkTree/SharedComponents";
import { AppleLogo } from "@/components/icons/AppleLogo";
import { FacebookLogo } from "@/components/icons/FacebookLogo";
import { GoogleLogo } from "@/components/icons/GoogleLogo";
import { SteamLogo } from "@/components/icons/SteamLogo";
import { Input } from "@/components/ui/input";
import { type LoginMethod, usePassportProvider } from "@/context";
import {
  Action,
  ControlType,
} from "@/context/AnalyticsProvider/AnalyticsProvider";
import { GamePageSection } from "@/context/AnalyticsProvider/gamePage";
import { useGameDetailsContext } from "@/context/GameDetailsContext";
import { useGameOnboardingContext } from "@/context/GameOnboardingContext";
import { useLoginModal } from "@/context/LoginModalContext";
import { cn } from "@/lib/utils";
import {
  inFacebookBrowser,
  isIOS,
  isTikTokBrowser,
  isXBrowser,
} from "@/utils/browser";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  LoaderCircle,
  MailIcon,
  TriangleAlert,
} from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SOCIAL_LOGINS, handleLoginLogic, validateEmail } from "./utils";

// Get login icon for a given login method
const getLoginIcon = (method: LoginMethod) => {
  if (method === "google") {
    return <GoogleLogo className="size-4 md:size-5" />;
  }
  if (method === "apple") {
    return <AppleLogo className="size-5 md:size-6" />;
  }
  if (method === "facebook") {
    return <FacebookLogo className="size-5 md:size-6" />;
  }
  return null;
};

export const OnboardingLinkTree = ({
  onNext: _onNext = () => {},
  isVisible: _isVisible = true,
  track = () => {},
}: Partial<GPOnboardingStepProps> & { isVisible?: boolean } = {}) => {
  const { gameData, allQuests } = useGameDetailsContext();
  const { isLoggedIn, loginDirectly, isLoggingIn } = usePassportProvider();
  const { setDisableLoadingModal } = useLoginModal();
  const { startSocialQuest, questState } = useGameOnboardingContext();
  const [showLinkSteam, setShowLinkSteam] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  // Track if we have already proceeded to prevent double redirects
  const [hasProceeded, setHasProceeded] = useState(false);
  const darkenVideoRef = useRef<HTMLVideoElement>(null);
  const goldenVideoRef = useRef<HTMLVideoElement>(null);

  // Find Steam wishlist quest
  const steamWishlistQuest = useMemo(
    () => allQuests?.find((quest) => quest.type === "steam_wishlist"),
    [allQuests],
  );

  // Action completion states
  const [isWishlistComplete, setIsWishlistComplete] = useState(false);
  const [isFollowXComplete, setIsFollowXComplete] = useState(false);
  const isLoginComplete = !!isLoggedIn;

  const isClaimUnlocked =
    isWishlistComplete || isFollowXComplete || isLoginComplete;

  // Steam App ID for 8 Ball Pool
  const STEAM_APP_ID = 3760100;
  const STEAM_WISHLIST_URL = `https://store.steampowered.com/app/${STEAM_APP_ID}/`;
  const LANDING_PAGE_PATH = "/games/8-ball/";

  // Helper function to check if user is in webview
  const isInWebview = useCallback(() => {
    return (
      inFacebookBrowser() || isTikTokBrowser() || (isXBrowser() && isIOS())
    );
  }, []);

  // Helper function to get testId from current URL
  const getTestIdFromUrl = useCallback(() => {
    if (typeof window === "undefined") return null;
    const searchParams = new URLSearchParams(window.location.search);
    let testId = searchParams.get("testId");
    const utmContent = searchParams.get("utm_content");
    if (utmContent) {
      const testIdMatch = utmContent.match(/_testId-(.+)$/);
      if (testIdMatch?.[1]) {
        testId = testIdMatch[1];
      }
    }
    return testId;
  }, []);

  // Helper function to redirect to landing page with testId, then open action URL
  const redirectToLandingThenOpenAction = useCallback(
    (actionUrl: string) => {
      const testId = getTestIdFromUrl();
      const landingUrl = new URL(
        LANDING_PAGE_PATH,
        window.location.origin,
      ).toString();

      // Build URL with testId, sessionId, and actionUrl parameters
      const params = new URLSearchParams();
      if (testId) {
        params.set("testId", testId);
      }
      params.set(
        "sessionId",
        localStorage.getItem("ajs_anonymous_id")?.replace(/"/g, "") || "",
      );
      params.set("openActionUrl", encodeURIComponent(actionUrl));

      const landingUrlWithParams = `${landingUrl}?${params.toString()}`;

      // Redirect to landing page - this will open external browser
      // The landing page will check for openActionUrl and open it
      if (isIOS()) {
        window.location.href = `x-safari-${landingUrlWithParams}`;
      } else if (/android/i.test(window.navigator.userAgent)) {
        window.location.href = `intent:${landingUrlWithParams}#Intent;end`;
      }
    },
    [getTestIdFromUrl],
  );

  const handleWishlistClick = () => {
    if (isWishlistComplete) return;

    track({
      action: Action.PRESSED,
      extras: {
        control: "steam-wishlist",
        controlType: ControlType.BUTTON,
        controlLabel: "Wishlist on Steam",
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });

    // If in webview, redirect to landing page first, then open Steam URL
    // If not in webview, just open Steam URL normally
    if (isInWebview()) {
      redirectToLandingThenOpenAction(STEAM_WISHLIST_URL);
    } else {
      window.open(STEAM_WISHLIST_URL, "_blank");
    }

    // Optimistically mark as complete after delay
    setTimeout(() => {
      setIsWishlistComplete(true);
    }, 1000);
  };

  const handleFollowXClick = () => {
    if (isFollowXComplete) return;

    track({
      action: Action.PRESSED,
      extras: {
        control: "follow-x",
        controlType: ControlType.BUTTON,
        controlLabel: "Follow on X",
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });

    const X_URL = "https://x.com/KamaCasino";

    // If in webview, redirect to landing page first, then open X URL
    // If not in webview, just open X URL normally
    if (isInWebview()) {
      redirectToLandingThenOpenAction(X_URL);
    } else {
      window.open(X_URL, "_blank");
    }

    // Optimistically mark as complete after delay
    setTimeout(() => {
      setIsFollowXComplete(true);
    }, 1000);
  };

  const handleLogin = useCallback(
    async (loginMethod: LoginMethod) => {
      // If in webview, redirect to landing page first, then proceed with login
      if (isInWebview()) {
        const testId = getTestIdFromUrl();
        const landingUrl = new URL(
          LANDING_PAGE_PATH,
          window.location.origin,
        ).toString();

        // Build URL with testId, sessionId, and loginAction parameters
        const params = new URLSearchParams();
        if (testId) {
          params.set("testId", testId);
        }
        params.set(
          "sessionId",
          localStorage.getItem("ajs_anonymous_id")?.replace(/"/g, "") || "",
        );
        params.set("loginAction", "true");
        // Store login method in localStorage since it might be complex (email object)
        const loginMethodStr =
          typeof loginMethod === "string"
            ? loginMethod
            : JSON.stringify(loginMethod);
        localStorage.setItem("pendingLoginMethod", loginMethodStr);

        const landingUrlWithParams = `${landingUrl}?${params.toString()}`;

        // Redirect to landing page first
        if (isIOS()) {
          window.location.href = `x-safari-${landingUrlWithParams}`;
        } else if (/android/i.test(window.navigator.userAgent)) {
          window.location.href = `intent:${landingUrlWithParams}#Intent;end`;
        }
        // Login will be handled after redirect on the landing page
        return;
      }

      // If not in webview, proceed with normal login flow
      await handleLoginLogic(loginMethod, {
        loginDirectly,
        track,
        setDisableLoadingModal,
        isLoggedIn,
      });
    },
    [
      loginDirectly,
      track,
      setDisableLoadingModal,
      isLoggedIn,
      isInWebview,
      getTestIdFromUrl,
    ],
  );

  const handleEmailSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const error = validateEmail(email);
      if (error) {
        setEmailError(error);
        return;
      }
      setEmailError(null);
      await handleLogin({ email });
    },
    [email, handleLogin],
  );

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value);
      setEmailError(null);
    },
    [],
  );

  const handleCloseEmail = useCallback(() => {
    setShowEmail(false);
    setEmailError(null);
  }, []);

  // Hide login prompt after successful login - just show the Claim button
  useEffect(() => {
    if (isLoggedIn && showLogin) {
      setShowLogin(false);
    }
  }, [isLoggedIn, showLogin]);

  // Show Link Steam step when both wishlist and login are complete
  useEffect(() => {
    if (isWishlistComplete && isLoginComplete && !hasProceeded) {
      setShowLinkSteam(true);
    }
  }, [isWishlistComplete, isLoginComplete, hasProceeded]);

  // Handle Steam linking
  const handleLinkSteamClick = useCallback(() => {
    if (!steamWishlistQuest || !gameData) return;

    track({
      action: Action.PRESSED,
      extras: {
        control: "link-steam",
        controlType: ControlType.BUTTON,
        controlLabel: "Link Steam to verify",
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });

    startSocialQuest(gameData, steamWishlistQuest);
  }, [steamWishlistQuest, gameData, startSocialQuest, track]);

  // Check if Steam is connected and redirect to landing page
  // Only proceeds when Steam is actually connected (questState?.state === "connected")
  useEffect(() => {
    if (!showLinkSteam || hasProceeded) return;

    if (
      questState?.quest.type === "steam_wishlist" &&
      questState?.state === "connected"
    ) {
      setHasProceeded(true);
      setIsWishlistComplete(true);
      // Redirect to landing page immediately after successful Steam linking
      // Use window.location to ensure immediate navigation and prevent modal flow switch
      window.location.href = "/games/8-ball/";
    }
  }, [questState, showLinkSteam, hasProceeded]);

  return (
    <div className="relative w-full bg-[#131313] grow text-white overflow-hidden md:overflow-visible font-suisse">
      {/* Background Layers */}
      <div className="absolute inset-0 z-0 md:overflow-visible">
        {/* Color Theme Background */}
        <div className="absolute inset-0">
          <img
            src={ASSETS.imgHybridRewards}
            alt=""
            className="absolute h-full w-full object-cover object-[65%_center]"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>

        {/* Link Tree Background (transparent overlay) */}
        <div className="absolute inset-0 overflow-visible -top-[20%] h-[120%]">
          <img
            src={ASSETS.imgLinkTreeBg}
            alt=""
            className="absolute h-full w-full object-contain scale-[125%] origin-top"
          />
        </div>
      </div>

      {/* Content Overlay */}
      <div className="relative z-0 flex flex-col h-full max-w-md mx-auto px-5 py-4 justify-end">
        {/* Top spacer: 15% on < 380px, 20% on sm, 30% on md and above */}
        {/* Group 1: Logo + Heading - positioned right below 25% spacer */}
        <div className="flex flex-col items-center flex-shrink-0">
          <Image
            src={ASSETS.imgLogo8Ball}
            className="object-contain"
            alt="Logo"
            width={300}
            height={140}
          />
          <h1 className="text-[26px] [@media(min-width:380px)]:text-[32px] md:text-[24px] [@media(min-width:380px)]:leading-10 md:leading-7 font-bold text-center text-white">
            $60k worth of prizes <br /> up for grabs!
          </h1>
        </div>

        {/* Middle section - Group 2 positioned higher */}
        <div className="flex flex-col">
          {/* Top spacer - smaller to push Group 2 up */}

          {/* Divider */}
          <div className="flex items-center gap-4 sm:gap-5 md:gap-6 my-3 sm:my-4 md:my-5">
            <div className="h-px flex-1 bg-[#fafafa]/20" />
            <span className="text-sm sm:text-base md:text-sm text-[#fafafa]">
              Choose how to get started
            </span>
            <div className="h-px flex-1 bg-[#fafafa]/20" />
          </div>

          {/* Action List */}
          <div className="flex flex-col gap-1.5">
            <ActionItem
              title="Wishlist on Steam"
              isCompleted={isWishlistComplete}
              rewardLabel="5x Entries"
              onClick={handleWishlistClick}
              icon={
                <SteamLogo className="size-4 [@media(min-width:400px)]:size-5 sm:size-5 md:size-3 text-white" />
              }
            />
            <ActionItem
              title="Follow on X"
              isCompleted={isFollowXComplete}
              rewardLabel="2x Entries"
              onClick={handleFollowXClick}
              icon={
                <Image
                  src="/assets/onboarding/x-icon.webp"
                  alt="X"
                  width={40}
                  height={40}
                  className="size-8 [@media(min-width:400px)]:size-9 sm:size-9 md:size-6"
                />
              }
            />
            <ActionItem
              title="Follow on Immutable"
              isCompleted={isLoginComplete}
              rewardLabel="3x Entries"
              onClick={() => {
                track({
                  action: Action.PRESSED,
                  extras: {
                    control: "follow-immutable",
                    controlType: ControlType.BUTTON,
                    controlLabel: "Follow on Immutable",
                    section: GamePageSection.ONBOARDING_MODAL,
                  },
                });
                setShowLogin(true);
              }}
              icon={
                <Image
                  src="/assets/onboarding/immutable-logo.webp"
                  alt="Immutable"
                  width={40}
                  height={40}
                  className="size-8 [@media(min-width:400px)]:size-9 sm:size-9 md:size-6"
                />
              }
            />
          </div>
        </div>

        {/* Group 3: Claim Button + Winners Ticker - directly on top of 10% bottom spacer */}
        <div className="flex flex-col flex-shrink-0 mt-4">
          {/* Claim Button, Link Steam Button, or Login Button */}
          {showLinkSteam ? (
            <div className="flex flex-col gap-3 items-center w-full">
              {/* Instruction text */}
              <p className="text-[12px] text-[#b6b6b6] font-medium text-center whitespace-pre">
                In order to verify your entry, please link your Steam account.
              </p>

              {/* Link Steam Button - only show if not already connected */}
              {questState?.state !== "connected" && (
                <button
                  type="button"
                  onClick={handleLinkSteamClick}
                  className="relative w-full h-[48px] bg-[#f7f7f7] text-[#0d0d0d] rounded-[26px] px-6 py-2 flex items-center justify-center gap-2 shadow-[0px_1px_3px_0px_rgba(0,0,0,0.16),0px_1px_3px_0px_rgba(0,0,0,0.32)] font-bold text-[18px] leading-[28px] transition-transform active:scale-[0.98]"
                >
                  Link Steam to verify
                </button>
              )}
            </div>
          ) : showLogin ? (
            <div className="flex flex-col gap-3 items-center w-full relative z-50">
              {/* Instruction text */}
              <p className="text-[12px] text-[#b6b6b6] font-medium text-center whitespace-pre">
                Please log in to verify and claim your rewards.
              </p>

              {/* Login Options */}
              <div className="relative w-full z-50">
                {/* Logging in */}
                <div
                  className={cn(
                    "absolute top-1/2 left-1/2 -translate-x-1/2 px-6 py-10 space-y-8 flex flex-col items-center transition-all duration-200 ease-in-out",
                    isLoggingIn || isLoggedIn
                      ? "opacity-100 -translate-y-1/2 pointer-events-auto"
                      : "opacity-0 translate-y-10 pointer-events-none",
                  )}
                >
                  <LoaderCircle className="size-6 animate-spin" />
                  <div className="text-center space-y-2">
                    <p className="font-bold text-white">Signing in</p>
                  </div>
                </div>

                {/* Sign in Options */}
                <div
                  className={cn(
                    "relative transition-all duration-200 ease-in-out z-10",
                    isLoggingIn || isLoggedIn
                      ? "opacity-0 translate-y-10 pointer-events-none"
                      : "opacity-100 pointer-events-auto",
                  )}
                >
                  <div
                    className={cn(
                      "flex w-full justify-evenly transition-all duration-300 ease-in-out relative z-10",
                      showEmail
                        ? "opacity-0 -translate-x-full pointer-events-none"
                        : "opacity-100 pointer-events-auto",
                    )}
                  >
                    {SOCIAL_LOGINS.map((method) => {
                      const methodStr = method as string;
                      return (
                        <button
                          key={methodStr}
                          type="button"
                          className="size-12 md:size-16 relative z-20 flex items-center justify-center rounded-full backdrop-blur-[20px] glass-border glass-border-hover active:scale-90 transition-transform duration-200 bg-white/5 pointer-events-auto"
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            track({
                              action: Action.PRESSED,
                              extras: {
                                control:
                                  `social-login-${methodStr}` as Lowercase<string>,
                                controlType: ControlType.BUTTON,
                                controlLabel: `Sign in with ${methodStr.charAt(0).toUpperCase() + methodStr.slice(1)}`,
                                section: GamePageSection.ONBOARDING_MODAL,
                              },
                            });
                            handleLogin(method);
                          }}
                        >
                          {getLoginIcon(method)}
                        </button>
                      );
                    })}

                    <button
                      key="email"
                      type="button"
                      className="size-12 md:size-16 relative z-20 flex items-center justify-center rounded-full backdrop-blur-[20px] glass-border glass-border-hover active:scale-90 transition-transform duration-200 bg-white/5 pointer-events-auto"
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        track({
                          action: Action.PRESSED,
                          extras: {
                            control: "email-login" as Lowercase<string>,
                            controlType: ControlType.BUTTON,
                            controlLabel: "Sign in with Email",
                            section: GamePageSection.ONBOARDING_MODAL,
                          },
                        });
                        setShowEmail(true);
                      }}
                    >
                      <MailIcon className="size-5 md:size-6" />
                    </button>
                  </div>

                  <form
                    onSubmit={handleEmailSubmit}
                    className={cn(
                      "w-full absolute top-0 transition-all duration-300 ease-in-out",
                      showEmail
                        ? "opacity-100 pointer-events-auto"
                        : "opacity-0 translate-x-full pointer-events-none",
                    )}
                    noValidate
                  >
                    <div className="flex items-center gap-x-2 w-full relative">
                      <button
                        type="button"
                        className="shrink-0 size-7 sm:size-8 rounded-full bg-[rgba(243,243,243,0.08)] flex items-center justify-center hover:bg-[rgba(243,243,243,0.12)] transition-colors"
                        aria-label="Back to login options"
                        onClick={handleCloseEmail}
                      >
                        <ArrowLeftIcon className="size-5 text-[#f3f3f3]" />
                      </button>
                      <div className="bg-[rgba(243,243,243,0.04)] border border-[rgba(243,243,243,0.16)] rounded-2xl grow flex items-center gap-2 px-2 sm:px-3 py-1 sm:py-2 focus-within:border-[rgba(243,243,243,0.5)] transition-colors">
                        <MailIcon className="size-4 sm:size-6 shrink-0 text-muted-foreground" />
                        <Input
                          type="email"
                          inputMode="email"
                          placeholder="Enter email"
                          value={email}
                          onChange={handleOnChange}
                          className="flex-1 bg-transparent px-2 border-0 text-base text-muted-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
                          autoComplete="email"
                        />
                        <button
                          type="submit"
                          className="shrink-0 size-7 sm:size-8 rounded-full bg-[rgba(243,243,243,0.08)] flex items-center justify-center hover:bg-[rgba(243,243,243,0.12)] transition-colors"
                          aria-label="Submit email"
                        >
                          <ArrowRightIcon className="size-5 text-[#f3f3f3]" />
                        </button>
                      </div>
                      <p
                        className={cn(
                          "text-sm text-red-500 h-5 flex items-center gap-x-2 left-10 top-full mt-2 absolute transition-opacity ease-in-out",
                          emailError ? "opacity-100" : "opacity-0",
                        )}
                      >
                        <TriangleAlert className="size-4" /> {emailError}
                      </p>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                track({
                  action: Action.PRESSED,
                  extras: {
                    control: "claim-rewards",
                    controlType: ControlType.BUTTON,
                    controlLabel: isClaimUnlocked
                      ? "Claim your rewards"
                      : "Complete at least one to unlock",
                    section: GamePageSection.ONBOARDING_MODAL,
                  },
                });

                if (!isLoginComplete) {
                  // Show login button if not logged in
                  setShowLogin(true);
                } else if (isWishlistComplete && isLoginComplete) {
                  // If both wishlist and login are complete, show Link Steam step
                  setShowLinkSteam(true);
                } else {
                  // If logged in but wishlist not complete, go directly to landing page
                  window.location.href = "/games/8-ball/";
                }
              }}
              disabled={!isClaimUnlocked}
              className="relative w-full mx-auto h-[75px] [@media(max-width:360px)]:h-[65px] md:h-[65px] rounded-[74px] overflow-visible group transition-all"
            >
              {/* Background Layer - Video */}
              <div className="absolute overflow-hidden rounded-[74px] inset-0">
                {/* Darken video - cross-fades out when unlocked */}
                <video
                  ref={darkenVideoRef}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className={cn(
                    "absolute inset-0 w-full h-full rounded-[74px] object-cover transition-opacity duration-1000 ease-in-out",
                    isClaimUnlocked
                      ? "opacity-0 pointer-events-none"
                      : "opacity-100",
                  )}
                >
                  <source
                    src="/assets/onboarding/darken-golden-button.mp4"
                    type="video/mp4"
                  />
                </video>
                {/* Golden video - cross-fades in when unlocked */}
                <video
                  ref={goldenVideoRef}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className={cn(
                    "absolute inset-0 w-full h-full rounded-[74px] object-cover scale-150 transition-opacity duration-1000 ease-in-out",
                    isClaimUnlocked
                      ? "opacity-100"
                      : "opacity-0 pointer-events-none",
                  )}
                >
                  <source
                    src="/assets/onboarding/golden-button.mp4"
                    type="video/mp4"
                  />
                </video>
                {/* Subtle dark overlay for golden video to make text stand out */}
                {isClaimUnlocked && (
                  <div className="absolute inset-0 bg-black/10 rounded-[74px]" />
                )}
              </div>

              {/* Darken overlay - only show when button is disabled */}
              {!isClaimUnlocked && (
                <div className="absolute h-[95px] [@media(max-width:360px)]:h-[82px] md:h-[82px] w-[289px] [@media(max-width:360px)]:w-[250px] md:w-[250px] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[2] pointer-events-none opacity-50">
                  <img
                    src={ASSETS.imgDarkenBg}
                    className="block max-w-none size-full"
                    alt=""
                  />
                </div>
              )}

              {/* Text - Exact Figma styling */}
              <span
                className={cn(
                  "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[3] pointer-events-none",
                  "font-suisse font-medium text-[20px] [@media(max-width:360px)]:text-[16px] md:text-[18px] leading-[28px] [@media(max-width:360px)]:leading-[22px] md:leading-[25px] text-center text-nowrap text-white whitespace-pre",
                  "[text-shadow:#683f23_2px_2px_0px]",
                  !isClaimUnlocked && "opacity-60",
                )}
              >
                {isClaimUnlocked
                  ? "Claim your rewards"
                  : "Complete at least one to unlock"}
              </span>
            </button>
          )}

          {/* Winners Ticker */}
          <div className="mt-3 flex items-center justify-center gap-2 px-5 overflow-hidden">
            <span className="text-[12px] [@media(max-width:360px)]:text-[10px] text-[#b6b6b6] font-medium whitespace-nowrap">
              Prizes won this week
            </span>
            <div className="text-[12px] [@media(max-width:360px)]:text-[10px] text-[#fafafa] font-medium whitespace-nowrap overflow-hidden text-ellipsis">
              Xbox, PlayStation 5, iPhone 17
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
